/**
 * Proposal helper functions
 */

import type {
  GmailSendPayload,
  ProposalDetail,
  ProposalExecutionErrorResult,
  ProposedAction,
} from '../core/types';

/**
 * Check if an action is a Gmail send action
 */
export function isGmailSendAction(
  action: ProposedAction
): action is ProposedAction<GmailSendPayload> {
  return action.proposed_action_type_name === 'gmail_send';
}

/**
 * Check if proposal has any Gmail actions
 */
export function hasGmailActions(proposal: ProposalDetail): boolean {
  return proposal.actions?.some(isGmailSendAction) ?? false;
}

/**
 * Get the primary action from a proposal
 */
export function getPrimaryAction(
  proposal: ProposalDetail
): ProposedAction | undefined {
  return proposal.actions?.[0];
}

/**
 * The version-stable identity of an action — the id of its ROOT version.
 *
 * A save past the in-place window mints a NEW row with a new id
 * (`ProposedAction.create_new_version`, apps/actions/models.py), so `action.id`
 * moves under you as the user edits. `parent_action` is NULL on the root and
 * points at the root — never at a predecessor, an invariant enforced in
 * `ProposedAction.clean` — so `parent_action ?? id` is constant for the whole
 * version chain.
 *
 * **Use this for React keys.** Keying a card, row or group on the raw `id`
 * makes React unmount and remount the subtree the moment a save lands, which
 * destroys focus, caret, scroll position and undo history — the recurring
 * "email editor loses focus while typing" bug. It is also the right key for
 * caches that must not move when a version is minted (see
 * {@link getTriggerActionIds}, which applies this same rule).
 *
 * It is NOT the right tool for "which row in this list is the one I'm holding?"
 * — use {@link isSameLogicalAction} for that. See its docs for why the two
 * notions are not interchangeable.
 */
export function getStableActionKey(action: ProposedAction): number {
  return action.parent_action ?? action.id;
}

/**
 * Do two rows refer to the same logical action?
 *
 * Three-tier identity check, applied in order:
 *
 * 1. **id match** — trivially the same row.
 * 2. **version-tree root** — `parent_action ?? id` is the id of the ROOT
 *    version. `parent_action` is NULL on the root and points AT the root on
 *    every child (`ProposedAction.clean`, apps/actions/models.py), an invariant
 *    enforced by the backend and serialized on every response. A save past the
 *    in-place window mints a NEW row with a new `id` AND renumbers
 *    `execution_order`, but the root id is constant for the whole version chain
 *    — so two rows with the same root are the same logical action even when both
 *    `id` and `execution_order` differ.
 * 3. **Fallback** — same slot (`execution_order`) and same type, ONLY for rows
 *    without a usable `parent_action` link (unpersisted/optimistic rows, which
 *    serialize no `parent_action` field at all). When either row carries the
 *    field (null on a root, the root id on a child), the root comparison in
 *    tier 2 is AUTHORITATIVE and its failure means "not the same action" —
 *    never fall through to the slot fallback. A rollover renumbers EVERY row's
 *    `execution_order` server-side while the client only receives the saved
 *    row, so a client-side sibling still holding its stale order can collide
 *    with the adopted row's new order. Falling through would adopt one save
 *    into two rows — duplicate version-root React keys, and a corrupted
 *    react-aria table collection (`RangeError: Invalid array length`).
 *
 * This is one of TWO distinct notions of action identity, and they are not
 * interchangeable:
 *
 * - **This function** answers *"which row in this list is the one I'm holding?"*
 *   — used for routing refreshes and reconciling updates, where the comparison
 *   is against a specific list that may have been rebuilt.
 * - **{@link getStableActionKey}** answers *"what is the stable identity of this
 *   action across all its versions?"* via `parent_action`, the root version id
 *   — used for React keys and cache keys, which must not move when a version is
 *   minted. (`getTriggerActionIds` in `../hooks/useTriggerPreview` applies the
 *   same rule to a proposal's primary action.)
 *
 * Call sites across this module (`multiActionTimeline.utils.ts`,
 * `multiActionTimeline.helpers.tsx`, `ActionGroupRenderer.tsx`,
 * `artifacts/EmailActionPreview.tsx`) import this helper rather than
 * inlining the rule.
 */
export function isSameLogicalAction(
  a: ProposedAction | undefined,
  b: ProposedAction | undefined
): boolean {
  if (!a || !b) {
    return false;
  }
  if (a.id === b.id) {
    return true;
  }
  // Version-tree root: parent_action is NULL on the root and points AT the root
  // on every child, so `parent_action ?? id` is constant across a rollover that
  // mints a new id and renumbers execution_order. Whenever either row actually
  // carries the field, this comparison is authoritative — a mismatch means
  // "different logical actions", full stop. Falling through to the slot
  // fallback here would false-match a sibling whose client-side
  // execution_order is stale after a rollover's server-side renumbering,
  // adopting one save into two rows (the duplicate-key react-aria crash).
  // This mirrors the guard the deleted `actionIdentity.isSameActionTree` had.
  if (a.parent_action !== undefined || b.parent_action !== undefined) {
    return (a.parent_action ?? a.id) === (b.parent_action ?? b.id);
  }
  // Fallback for rows without a usable parent link (unpersisted/optimistic rows).
  return (
    a.execution_order === b.execution_order &&
    a.proposed_action_type_name === b.proposed_action_type_name
  );
}

/**
 * Get the primary artifact from a proposal (alias for getPrimaryAction)
 * @deprecated Use getPrimaryAction instead
 */
export function getPrimaryArtifact(
  proposal: ProposalDetail
): ProposedAction | undefined {
  return getPrimaryAction(proposal);
}

/**
 * Check if a proposal is informational-only (no executable actions).
 *
 * Informational proposals are typically AI-generated research or recommendations
 * that require user acknowledgment but don't trigger any automated actions
 * (like sending emails or creating calendar events).
 *
 * Examples:
 * - Lead research recommendations ("Approve New Lead - Rippling")
 * - Market analysis summaries
 * - Customer health assessments
 */
export function isInformationalProposal(proposal: ProposalDetail): boolean {
  return !proposal.actions || proposal.actions.length === 0;
}

/**
 * Type guard for the proposal-level execution error structure
 * (`execution_result` of a failed run). Shared by the approval footers
 * (StickyFooter, InlineProposalFooter) to classify polling results.
 */
export function isExecutionError(
  result: unknown
): result is ProposalExecutionErrorResult {
  return (
    typeof result === 'object' &&
    result !== null &&
    'error' in result &&
    typeof (result as Record<string, unknown>).error === 'string' &&
    'error_type' in result &&
    typeof (result as Record<string, unknown>).error_type === 'string'
  );
}
